import {Checkbox, styled, Tooltip} from 'bigdatr-style';
import {useAnalytics, Text} from 'bigdatr-style';
import {Box, Flex, FlexColumn, Grid} from 'bigdatr-style/layout';
import React from 'react';
import {ChevronSmallDown, LockOn} from 'react-swm-icon-pack';
import IconAU from './flags/IconAU';
import IconNZ from './flags/IconNZ';
import IconCA from './flags/IconCA';
import IconGB from './flags/IconGB';
import IconUS from './flags/IconUS';
import IconAR from './flags/IconAR';
import IconBE from './flags/IconBE';
import IconBR from './flags/IconBR';
import IconCH from './flags/IconCH';
import IconDE from './flags/IconDE';
import IconDK from './flags/IconDK';
import IconES from './flags/IconES';
import IconFI from './flags/IconFI';
import IconFR from './flags/IconFR';
import IconHK from './flags/IconHK';
import IconID from './flags/IconID';
import IconIN from './flags/IconIN';
import IconIT from './flags/IconIT';
import IconJP from './flags/IconJP';
import IconMX from './flags/IconMX';
import IconNL from './flags/IconNL';
import IconNO from './flags/IconNO';
import IconPH from './flags/IconPH';
import IconPR from './flags/IconPR';
import IconRU from './flags/IconRU';
import IconSE from './flags/IconSE';
import IconSG from './flags/IconSG';
import IconTW from './flags/IconTW';
import IconVN from './flags/IconVN';
import IconZA from './flags/IconZA';

const COUNTRY_DISPLAY_NAMES = [
    'Australia',
    'New Zealand',
    'Canada',
    'United Kingdom',
    'United States',
    'Argentina',
    'Belgium',
    'Brazil',
    'Switzerland',
    'Germany',
    'Denmark',
    'Spain',
    'Finland',
    'France',
    'Hong Kong',
    'Indonesia',
    'India',
    'Italy',
    'Japan',
    'Mexico',
    'Netherlands',
    'Norway',
    'Philippines',
    'Puerto Rico',
    'Russia',
    'Sweden',
    'Singapore',
    'Taiwan',
    'Vietnam',
    'South Africa'
] as const;

export type CountryDisplayName = (typeof COUNTRY_DISPLAY_NAMES)[number];

export const countryIcons: Record<CountryDisplayName, React.ReactNode> = {
    Australia: <IconAU />,
    'New Zealand': <IconNZ />,
    Canada: <IconCA />,
    'United Kingdom': <IconGB />,
    'United States': <IconUS />,
    Argentina: <IconAR />,
    Belgium: <IconBE />,
    Brazil: <IconBR />,
    Switzerland: <IconCH />,
    Germany: <IconDE />,
    Denmark: <IconDK />,
    Spain: <IconES />,
    Finland: <IconFI />,
    France: <IconFR />,
    'Hong Kong': <IconHK />,
    Indonesia: <IconID />,
    India: <IconIN />,
    Italy: <IconIT />,
    Japan: <IconJP />,
    Mexico: <IconMX />,
    Netherlands: <IconNL />,
    Norway: <IconNO />,
    Philippines: <IconPH />,
    'Puerto Rico': <IconPR />,
    Russia: <IconRU />,
    Sweden: <IconSE />,
    Singapore: <IconSG />,
    Taiwan: <IconTW />,
    Vietnam: <IconVN />,
    'South Africa': <IconZA />
};

export const CountryDropdownFloater = styled(Box)<{$width?: string}>`
    ${(props) => {
        const {borderRadiuses, colors, boxShadows} = props.theme;
        const width = props.$width ?? '15rem';
        return `
            width: ${width};
            border: 1px solid ${colors.outline};
            border-radius: ${borderRadiuses.round};
            background-color: ${colors.background};
            box-shadow: ${boxShadows.popup};
            display: flex;
            flex-direction: column;
            gap: .5rem;
            overflow-y: auto;
            max-height: clamp(10vh, 30rem, 75vh);
        `;
    }}
`;

export function CountryGroup({children, region}: {children: React.ReactNode; region: string}) {
    return (
        <FlexColumn>
            <Text textStyle="muted">{region}</Text>
            <FlexColumn gap={2}>{children}</FlexColumn>
        </FlexColumn>
    );
}

export function CountryChoice({
    country,
    checked,
    locked,
    onChange
}: {
    country: string | CountryDisplayName;
    checked?: boolean;
    onChange?: () => void;
    locked?: boolean;
}) {
    const analytics = useAnalytics();
    const icon = countryIcons[country] ?? null;
    return (
        <Tooltip
            maxWidth="max-content"
            content={locked ? 'To access this feature, contact us.' : null}
        >
            <Grid
                data-testid="country-switcher-option"
                as="label"
                cursor="pointer"
                gridTemplateColumns="1.75rem auto"
                alignItems="center"
                gap={3}
                onClick={locked ? () => analytics.chat() : undefined}
            >
                {icon}
                {locked ? (
                    <Flex alignItems="center" justifyContent="space-between" gap={2}>
                        <Text textStyle="muted">{country}</Text>
                        <Grid
                            placeItems="center"
                            borderWidth="1px"
                            borderStyle="solid"
                            borderColor="background3"
                            size="1.25rem"
                            borderRadius="10rem"
                        >
                            <LockOn size="0.75rem" data-testid="country-lock-icon" />
                        </Grid>
                    </Flex>
                ) : (
                    <Checkbox
                        wrappingElement="div"
                        value={checked}
                        onChange={onChange ?? (() => {})}
                        radio
                        label={country}
                        layoutProps={{
                            fontWeight: 'unset',
                            width: '100%',
                            justifyContent: 'space-between'
                        }}
                    />
                )}
            </Grid>
        </Tooltip>
    );
}

const ChevronWrapper = styled.span<{$open: boolean}>`
    display: inline-flex;
    transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
    transition: transform 0.2s ease;
`;

export function CountryDropdownTrigger({
    country,
    open = false
}: {
    country: string | CountryDisplayName;
    open?: boolean;
}) {
    const icon = countryIcons[country] ?? null;
    return (
        <Grid gridTemplateColumns="2rem auto 1.5rem" alignItems="center">
            {icon}
            <Box ml={2} mr={1}>
                {country}
            </Box>
            <ChevronWrapper $open={open}>
                <ChevronSmallDown />
            </ChevronWrapper>
        </Grid>
    );
}
